import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQuery } from "@/graphql";
import { GET_EXCLUSION_LISTS, GET_EXCLUSION_LIST, CREATE_EXCLUSION_LIST, UPDATE_EXCLUSION_LIST, DELETE_EXCLUSION_LIST } from "@/graphql/product-catalogue/exclusion-lists";

export interface ExclusionList {
  _id: string;
  name: string;
  targetColumnIds?: string[];
  codes?: string[];
  effectRate?: number;
  active?: boolean;
  startDate?: string;
  endDate?: string;
  codesCount?: number;
  targetColumnCount?: number;
}

export interface ExclusionListsResponse {
  data: ExclusionList[];
  meta: { countDocs: number; pageNo: number; pageSize: number };
}

export interface GetExclusionListParams { id: string }

export interface ExclusionListInput {
  name: string;
  targetColumnIds: string[];
  codes: string[];
  effectRate?: number;
  active?: boolean;
  startDate?: string;
  endDate?: string;
}

export interface ExclusionListUpdateInput {
  name?: string;
  targetColumnIds?: string[];
  codes?: string[];
  effectRate?: number;
  active?: boolean;
  startDate?: string;
  endDate?: string;
}

export const exclusionListsApi = createApi({
  reducerPath: "api/exclusionLists",
  baseQuery,
  tagTypes: ["ExclusionList"],
  endpoints: (builder) => ({
    getExclusionList: builder.query<ExclusionList, GetExclusionListParams>({
      query: ({ id }) => ({
        document: GET_EXCLUSION_LIST,
        variables: { id },
      }),
      transformResponse: (r: { exclusionList: ExclusionList }) => r.exclusionList,
      providesTags: (_res, _err, { id }) => [{ type: "ExclusionList", id }],
    }),
    getExclusionLists: builder.query<ExclusionListsResponse, { pageNo?: number; pageSize?: number; active?: boolean }>({
      query: (variables) => ({
        document: GET_EXCLUSION_LISTS,
        variables,
      }),
      transformResponse: (r: { exclusionLists: ExclusionListsResponse }) => r.exclusionLists,
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ _id }) => ({ type: "ExclusionList" as const, id: _id })),
              { type: "ExclusionList" as const, id: "LIST" },
            ]
          : [{ type: "ExclusionList" as const, id: "LIST" }],
    }),
    createExclusionList: builder.mutation<ExclusionList, { input: ExclusionListInput }>({
      query: ({ input }) => ({
        document: CREATE_EXCLUSION_LIST,
        variables: { input },
      }),
      transformResponse: (r: { createExclusionList: ExclusionList }) => r.createExclusionList,
      invalidatesTags: [{ type: "ExclusionList", id: "LIST" }],
    }),
    updateExclusionList: builder.mutation<ExclusionList, { id: string; input: ExclusionListUpdateInput }>({
      query: ({ id, input }) => ({
        document: UPDATE_EXCLUSION_LIST,
        variables: { id, input },
      }),
      transformResponse: (r: { updateExclusionList: ExclusionList }) => r.updateExclusionList,
      invalidatesTags: (_res, _err, { id }) => [{ type: "ExclusionList", id }, { type: "ExclusionList", id: "LIST" }],
    }),
    deleteExclusionList: builder.mutation<boolean, { id: string }>({
      query: ({ id }) => ({
        document: DELETE_EXCLUSION_LIST,
        variables: { id },
      }),
      transformResponse: (r: { deleteExclusionList: boolean }) => r.deleteExclusionList,
      invalidatesTags: [{ type: "ExclusionList", id: "LIST" }],
    }),
  }),
});

export const {
  useGetExclusionListQuery,
  useGetExclusionListsQuery,
  useCreateExclusionListMutation,
  useUpdateExclusionListMutation,
  useDeleteExclusionListMutation,
} = exclusionListsApi;


